import { Injectable, OnDestroy, inject } from "@angular/core";
import { CartProduct } from "../Models/cart-product";
import {
  BehaviorSubject,
  catchError,
  forkJoin,
  map,
  Observable,
  of,
  skip,
  Subscription,
  switchMap,
  take,
} from "rxjs";
import { CartService } from "./cart.service";
import { LanguageService } from "./language.service";
import { ProductConfigurationService } from "./product-configuration.service";
import { ProductConfiguration } from "../Models/product-configuration";
import { cartLocal } from "../Pages/products/detail-page/detail-page.component";
import { StorageService } from "./storage.service";

const CART_STORAGE_KEY = "carts";

const EMPTY_CART: cartLocal = { idCart: 0, CartProduct: [] };

@Injectable({
  providedIn: "root",
})
export class CartOffLineService implements OnDestroy {
  // declared before cartSubject, whose initial value is read from the storage
  private readonly storage = inject(StorageService);

  private readonly cartSubject = new BehaviorSubject<cartLocal>(
    this.loadFromStorage(),
  );

  /** Public observable — subscribe to get reactive cart updates */
  readonly cart$ = this.cartSubject.asObservable();

  /**
   * @deprecated Use cart$ instead. Kept for backward compatibility.
   * Always reflects the latest cart products.
   */
  get cart(): Observable<cartLocal> {
    return this.cart$;
  }

  /** Snapshot of the current cart products (always up-to-date) */
  get cartProducts(): CartProduct[] {
    return this.cartSubject.value.CartProduct ?? [];
  }

  private readonly storageSyncSub: Subscription;

  constructor(
    private readonly cartService: CartService,
    private readonly productConfigurationService: ProductConfigurationService,
    languageService: LanguageService,
  ) {
    // Single source of truth: sync to localStorage whenever the subject changes
    this.storageSyncSub = this.cartSubject.subscribe((cart) => {
      this.storage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
    });

    // the configurations kept in localStorage are in the language they were added in: fetch them again when it changes
    languageService.language$
      .pipe(
        skip(1),
        switchMap(() => this.reloadConfigurations()),
      )
      .subscribe();
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Add a product to the offline cart.
   * If the same configuration already exists, its quantity is incremented
   * (capped at the available stock). Returns whether the quantity was capped.
   */
  addCarts(
    cartProduct: CartProduct,
  ): Observable<{ success: boolean; quantityCapped: boolean }> {
    return this.cart$.pipe(
      take(1),
      map((currentCart) => {
        const products = [...(currentCart.CartProduct ?? [])];
        let quantityCapped = false;

        const existingIndex = products.findIndex(
          (p) => p.configuration?.id === cartProduct.configuration?.id,
        );

        if (existingIndex > -1) {
          const existing = products[existingIndex];
          const currentQty = existing.quantity ?? 0;
          const maxQty = existing.configuration?.quantity ?? 0;
          const newQty = Math.min(
            currentQty + (cartProduct.quantity ?? 1),
            maxQty,
          );

          quantityCapped = currentQty + (cartProduct.quantity ?? 1) > maxQty;
          products[existingIndex] = { ...existing, quantity: newQty };
        } else {
          products.push({ ...cartProduct });
        }

        this.cartSubject.next({ ...currentCart, CartProduct: products });
        return { success: true, quantityCapped };
      }),
    );
  }

  /**
   * Remove a product from the offline cart by configuration id.
   */
  deleteCarts(cartProduct: CartProduct): Observable<void> {
    return this.cart$.pipe(
      take(1),
      map((currentCart) => {
        // Use configuration?.id to stay consistent with addCarts
        const targetId =
          cartProduct.configuration?.id ?? cartProduct.configurationId;
        const updated = (currentCart.CartProduct ?? []).filter(
          (p) => (p.configuration?.id ?? p.configurationId) !== targetId,
        );
        this.cartSubject.next({ ...currentCart, CartProduct: updated });
      }),
    );
  }

  /**
   * Update the quantity of an existing cart product.
   */
  updateCarts(cartProduct: CartProduct): Observable<void> {
    return this.cart$.pipe(
      take(1),
      map((currentCart) => {
        const products = [...(currentCart.CartProduct ?? [])];
        const targetId =
          cartProduct.configuration?.id ?? cartProduct.configurationId;
        const index = products.findIndex(
          (p) => (p.configuration?.id ?? p.configurationId) === targetId,
        );

        if (index > -1) {
          products[index] = {
            ...products[index],
            quantity: cartProduct.quantity,
          };
          this.cartSubject.next({ ...currentCart, CartProduct: products });
        }
      }),
    );
  }

  /**
   * Sync all offline cart products to the backend, then clear the local cart.
   * Each product is added individually; errors are logged per product.
   */
  addToCartOnline(): void {
    const products = this.cartProducts; // snapshot before clearing

    if (!products.length) return;

    products.forEach((product) => {
      this.cartService
        .add(product.configuration!, product.quantity!)
        .subscribe({
          next: () => {},
          error: (err) =>
            console.error("Failed to sync product to server:", err),
        });
    });

    // Clear local cart immediately after dispatching requests
    this.clearCart();
  }

  /**
   * Remove all products from the offline cart and clear localStorage.
   */
  clearCart(): void {
    this.cartSubject.next({ ...EMPTY_CART });
  }

  ngOnDestroy(): void {
    this.storageSyncSub.unsubscribe();
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Fetches the configuration of each cart product again (in the current language). Quantities are kept,
   * and products removed meanwhile aren't added back.
   */
  private reloadConfigurations(): Observable<void> {
    const ids = [
      ...new Set(
        this.cartProducts
          .map((p) => p.configuration?.id ?? p.configurationId)
          .filter((id): id is number => id != null),
      ),
    ];
    if (!ids.length) return of(undefined);

    return forkJoin(
      ids.map((id) =>
        this.productConfigurationService
          .getByConfigurationId(id)
          .pipe(catchError(() => of(null))),
      ),
    ).pipe(
      map((configurations) => {
        const byId = new Map(
          configurations
            .filter((c): c is ProductConfiguration => !!c)
            .map((c) => [c.id, c]),
        );
        const currentCart = this.cartSubject.value;
        const products = (currentCart.CartProduct ?? []).map((p) => {
          const configuration = byId.get(p.configuration?.id ?? p.configurationId);
          return configuration ? { ...p, configuration } : p;
        });
        this.cartSubject.next({ ...currentCart, CartProduct: products });
      }),
    );
  }

  private loadFromStorage(): cartLocal {
    try {
      const raw = this.storage.getItem(CART_STORAGE_KEY);
      if (!raw) return { ...EMPTY_CART };

      const parsed = JSON.parse(raw);

      // Migrate old format (plain array) to new object format
      if (Array.isArray(parsed)) {
        return { idCart: 0, CartProduct: parsed };
      }

      if (parsed && Array.isArray(parsed.CartProduct)) {
        return parsed as cartLocal;
      }
    } catch (err) {
      console.error("Corrupted cart in localStorage, resetting.", err);
      this.storage.removeItem(CART_STORAGE_KEY);
    }

    return { ...EMPTY_CART };
  }
}
